import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { type Ref, useState } from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import type { CollectionDef } from '@/core/dcr/fields/types';
import { serializeItem } from '@/core/dcr/serialize/toDcrInput';

import { useDcrFormContext } from '../_context/DcrFormContext';

import { CollectionItemFields } from './CollectionItemFields';
import { collectionCardStyles as sx } from './Collections.styles';
import { getCollectionItemTitle, getNewItemTitle } from './collectionTitle';

type Props = {
	collectionKey: string;
	collection: CollectionDef;
	index: number;
	isLastItem: boolean;
	onRemove: () => void;
	cardRef?: Ref<HTMLDivElement>;
};

export const CollectionCard = ({
	collectionKey,
	collection,
	index,
	isLastItem,
	onRemove,
	cardRef
}: Props) => {
	const { t } = useTranslation();
	const { picklists } = useDcrFormContext();
	const {
		control,
		formState: { errors }
	} = useFormContext();
	const [expanded, setExpanded] = useState(true);

	const path = `${collectionKey}.${index}`;
	const item = useWatch({ control, name: path }) as
		Record<string, unknown> | undefined;

	const title =
		getCollectionItemTitle(collection.id, item, picklists) ||
		getNewItemTitle(collection.id, t);
	const hasValues = !!item && !!serializeItem(collection.fields, item);
	const hasError = !!get(errors, path);

	return (
		<Box ref={cardRef} sx={sx.root}>
			<Box
				sx={() => sx.header(isLastItem)}
				onClick={() => setExpanded(open => !open)}
			>
				<Box sx={sx.headerContent(hasValues, hasError)}>
					<ExpandMoreIcon sx={sx.expandIcon(expanded)} />
					<Typography dir='auto' sx={sx.headerText}>
						{title}
					</Typography>
				</Box>

				<Box sx={sx.removeButton} onClick={e => e.stopPropagation()}>
					<RemoveFormFieldButton type='button' onClick={onRemove} />
				</Box>
			</Box>

			<Collapse in={expanded}>
				<Box sx={sx.content}>
					<CollectionItemFields
						collectionKey={collectionKey}
						collectionId={collection.id}
						path={path}
					/>
				</Box>
			</Collapse>
		</Box>
	);
};
